// Spoken replies with the browser's own voices (no server, no cost). The choice is per device.
export type SpeakMode = "never" | "voice" | "always";
const KEY = "ada.speak";

export const speech = {
  supported: () => "speechSynthesis" in window,
  mode: (): SpeakMode => (localStorage.getItem(KEY) as SpeakMode | null) ?? "voice",
  setMode: (m: SpeakMode) => localStorage.setItem(KEY, m),
  speaking: () => speech.supported() && speechSynthesis.speaking,
  stop: () => { if (speech.supported()) speechSynthesis.cancel(); },
  /** iOS only lets a page speak after a user gesture; call this from one (the mic or send button) so the reply can be read later. */
  unlock() {
    if (!speech.supported() || speech.mode() === "never") return;
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    speechSynthesis.speak(u);
  },
  /** Reads plain text aloud in Spanish; `onEnd` fires when it finishes or is stopped. */
  say(text: string, onEnd?: () => void) {
    if (!speech.supported()) return;
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(plain(text));
    u.lang = "es-ES";
    const voices = speechSynthesis.getVoices().filter((v) => v.lang.startsWith("es"));
    const voice = voices.find((v) => v.lang === "es-ES" && /natural|premium|enhanced|google/i.test(v.name)) ?? voices.find((v) => v.lang === "es-ES") ?? voices[0];
    if (voice) u.voice = voice;
    u.rate = 1.05;
    u.onend = () => onEnd?.();
    u.onerror = () => onEnd?.();
    speechSynthesis.speak(u);
  },
};

/** Markdown and codes read badly aloud: drop the marks and spell locators by groups. */
const plain = (md: string) => md
  .replace(/```[\s\S]*?```/g, "")
  .replace(/[*_`#>]/g, "")
  .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
  .replace(/\b([A-Z]{3})-([0-9A-F]{6})\b/g, (_m, a: string, b: string) => `${a.split("").join(" ")}, ${b.split("").join(" ")}`)
  .replace(/\s+/g, " ")
  .trim();
